import React, { createContext, useContext, useState } from 'react';

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

// Translation dictionary
const translations = {
  en: {
    // Navigation
    home: 'Home',
    records: 'Records',
    profile: 'Profile',
    admin: 'Admin',
    
    // Common
    loading: 'Loading...',
    save: 'Save',
    cancel: 'Cancel',
    edit: 'Edit',
    update: 'Update',
    delete: 'Delete',
    confirm: 'Confirm',
    
    // Home Page
    yourPoints: 'Your Points',
    keepEarning: 'Keep earning to unlock rewards!',
    memberQRCode: 'Your Member QR Code',
    showQRCode: 'Show this QR code to staff to earn or redeem points',
    currentBranch: 'Current Branch',
    rewards: 'Rewards',
    browseRewards: 'Browse available prizes',
    scan: 'Scan',
    scanItems: 'Scan items to earn points',
    
    // Records Page
    transactionRecords: 'Transaction Records',
    all: 'All',
    earned: 'Earned',
    redeemed: 'Redeemed',
    currentBalance: 'Current Balance',
    points: 'Points',
    noTransactions: 'No transactions found',
    startEarning: 'Start earning points to see your transaction history!',
    totalEarned: 'Total Earned',
    totalRedeemed: 'Total Redeemed',
    balance: 'Balance',
    
    // Profile Page
    profile: 'Profile',
    editProfile: 'Edit Profile',
    personalInfo: 'Personal Information',
    username: 'Username',
    fullName: 'Full Name',
    gender: 'Gender',
    selectGender: 'Select Gender',
    male: 'Male',
    female: 'Female',
    other: 'Other',
    birthday: 'Birthday',
    phoneNumber: 'Phone Number',
    email: 'Email',
    saveChanges: 'Save Changes',
    saving: 'Saving...',
    security: 'Security',
    changePassword: 'Change Password',
    currentPassword: 'Current Password',
    newPassword: 'New Password',
    confirmPassword: 'Confirm New Password',
    updating: 'Updating...',
    updatePassword: 'Update Password',
    customerSupport: 'Customer Support',
    aiAssistant: 'AI Assistant',
    instantHelp: 'Get instant help with your points and rewards',
    languageSupport: 'Supports Chinese & English',
    chatNow: 'Chat Now',
    contactUs: 'Contact Us',
    whatsappContact: 'WhatsApp Support',
    phoneContact: 'Phone Support',
    selectBranch: 'Select Branch',
    
    // Admin Dashboard
    adminDashboard: 'Admin Dashboard',
    scanner: 'Scanner',
    redemption: 'Redemption',
    members: 'Members',
    prizes: 'Prizes',
    items: 'Items',
    settings: 'Settings',
    qrScanner: 'QR Code Scanner',
    scanUserQR: 'Scan User QR',
    scanItemBarcode: 'Scan Item Barcode',
    memberSearch: 'Member Search',
    searchPlaceholder: 'Search by name or username...',
    viewHistory: 'View History',
    prizeManagement: 'Prize Management',
    stock: 'Stock',
    systemSettings: 'System Settings',
    aiChatbot: 'AI Chatbot',
    enableChatbot: 'Enable or disable the AI chatbot for customer support',
    whatsappIntegration: 'WhatsApp Integration',
    enableWhatsapp: 'Enable WhatsApp chatbot integration via Twilio',
    instagramIntegration: 'Instagram Integration',
    enableInstagram: 'Enable Instagram DM chatbot integration via Facebook Graph API',
    currentConfig: 'Current Configuration',
    enabled: 'Enabled',
    disabled: 'Disabled',
    branches: 'Branches',
    branchManagement: 'Branch Management',
    addBranch: 'Add Branch',
    branchName: 'Branch Name',
    branchLocation: 'Branch Location',
    contactInfo: 'Contact Information',
    whatsappNumber: 'WhatsApp Number',
    phoneNumber2: 'Phone Number',
    
    // Messages
    profileUpdated: 'Profile updated successfully!',
    passwordUpdated: 'Password updated successfully!',
    settingsUpdated: 'Settings updated successfully!',
    branchAdded: 'Branch added successfully!',
    branchUpdated: 'Branch updated successfully!',
    passwordMismatch: 'New passwords do not match',
    accessDenied: 'Access Denied',
    noAdminPrivileges: "You don't have admin privileges.",
    
    // Chatbot
    aiAssistantDemo: 'AI Assistant (Demo)',
    online: 'Online',
    typeMessage: 'Type a message...',
    send: 'Send',
    quickActions: 'Quick actions:',
    checkPoints: 'Check Points',
    availablePrizes: 'Available Prizes',
    pointsExpiry: 'Points Expiry',
    call: 'Call',
    
    // Layout & Navigation
    welcome: 'Welcome',
    signOut: 'Sign Out',
    demo: 'Demo',
    
    // Admin Scanner
    demoClickToSimulate: 'Demo: Click to simulate scanning',
    simulateScan: 'Simulate Scan',
    scanResult: 'Scan Result',
    addPoints: 'Add Points',
    redeemPoints: 'Redeem Points',
    
    // Branch Management
    edit: 'Edit',
    analytics: 'Analytics',
    addNewBranch: 'Add New Branch',
    
    // Form Labels
    name: 'Name',
    location: 'Location',
    whatsapp: 'WhatsApp',
    phone: 'Phone',
    
    // Branch Actions
    editBranch: 'Edit Branch',
    deleteBranch: 'Delete Branch',
    confirmDelete: 'Are you sure you want to delete',
    branchDeleted: 'Branch deleted successfully!',
    
    // User Management
    users: 'Users',
    userManagement: 'User Management',
    addUser: 'Add User',
    editUser: 'Edit User',
    deleteUser: 'Delete User',
    userRole: 'User Role',
    admin: 'Admin',
    sales: 'Sales',
    member: 'Member',
    showAll: 'Show All',
    loadingMembers: 'Loading members...',
    noMembersFound: 'No members found',
    totalMembers: 'Total Members',
    
    // Member Management
    memberManagement: 'Member Management',
    addMember: 'Add Member',
    editMember: 'Edit Member',
    deleteMember: 'Delete Member',
    memberDetails: 'Member Details',
    
    // Prize & Item Management
    addPrize: 'Add Prize',
    editPrize: 'Edit Prize',
    deletePrize: 'Delete Prize',
    prizeName: 'Prize Name',
    costPoints: 'Cost Points',
    initialStock: 'Initial Stock',
    description: 'Description',
    addItem: 'Add Item',
    editItem: 'Edit Item',
    deleteItem: 'Delete Item',
    itemName: 'Item Name',
    itemManagement: 'Item Management',
    barcode: 'Barcode',
    pointsValue: 'Points Value',
    category: 'Category',
    food: 'Food',
    beverage: 'Beverage',
    dessert: 'Dessert',
    other: 'Other',
    initialPoints: 'Initial Points',
    password: 'Password',
    
    // App Branding
    appName: 'ClawPoints 爪爪積分',
    appSlogan: 'Play, Earn, Redeem with AI',
    
    // Login Page
    membershipPoints: 'ClawPoints 爪爪積分',
    demoPreview: 'Demo Preview',
    loginAsMember: 'Login as Demo User (Member)',
    loginAsSales: 'Login as Demo Sales',
    loginAsAdmin: 'Login as Demo Admin (Owner)',
    demoNotice: 'This is a demo preview. No real authentication required.',
    orLoginWithCredentials: 'Or login with member credentials',
    memberLoginExample: 'Member Login Example',
    
    // Pagination
    showing: 'Showing',
    of: 'of',
    previous: 'Previous',
    next: 'Next',
    page: 'Page',
    
    // Search
    searchMembers: 'Search Members',
    searchPrizes: 'Search Prizes',
    searchItems: 'Search Items',
    searchByName: 'Search by name...',
    searchByNameOrBarcode: 'Search by name or barcode...',
    noResults: 'No results found',
    clearSearch: 'Clear Search',
    results: 'results',
    for: 'for',
    totalPrizes: 'Total Prizes',
    totalItems: 'Total Items',
    
    // Home Page Modals
    availableRewards: 'Available Rewards',
    redeem: 'Redeem',
    redeemSuccess: 'Successfully redeemed',
    insufficientPoints: 'Insufficient Points',
    need: 'Need',
    outOfStock: 'Out of Stock',
    scanToEarn: 'Scan to Earn Points',
    scanInstructions: 'Scan Item Barcode',
    scanDescription: 'Point your camera at any product barcode to earn points instantly!',
    scanSuccess: 'Scan successful! Earned',
    scanTip: 'In a real app, this would use your camera to scan barcodes',
    
    // My Account
    myAccount: 'My Account',
    accountInfo: 'Account Information',
    passwordManagement: 'Manage your login password and security settings',
    accountActions: 'Account Actions',
    updateProfile: 'Update Profile Information',
    refreshSession: 'Refresh Login Session',
    sessionRefreshed: 'Session refreshed successfully',
    
    // Scanner Business Flow
    memberScanned: 'Member QR Code Scanned',
    itemScanned: 'Item Barcode Scanned',
    selectAction: 'Select Action',
    earnPoints: 'Earn Points',
    redeemPrize: 'Redeem Prize',
    scanItemToEarn: 'Scan item barcode to add points',
    selectPrizeToRedeem: 'Choose prize to redeem',
    selectItemScanned: 'Select the item that was scanned',
    nowScanMemberQR: 'Now scan the member\'s QR code to award points',
    scanMemberQR: 'Scan Member QR Code',
    itemNotFound: 'Item Not Found',
    itemNotInDatabase: 'This barcode is not in the database',
    addToDatabase: 'Add to Database',
    scanAgain: 'Scan Again',
    simulateUnknown: 'Simulate Unknown Item',
    
    // Transaction History
    transactionHistory: 'Transaction History',
    currentPoints: 'Current Points',
    totalTransactions: 'Total Transactions',
    currentBalance: 'Current Balance',
    earned: 'Earned',
    redeemed: 'Redeemed',
    
    // Prize Business Flow
    prizeFlow: 'Prize Flow',
    memberFlow: 'Member Flow',
    prizeFlowDesc: '1. Scan prize barcode → 2. Scan member QR → Add points',
    memberFlowDesc: '1. Scan member QR → 2. Choose action → Complete transaction',
    scanPrizeBarcode: 'Scan Prize Barcode',
    scanBarcode: 'Scan Barcode',
    prizeScanned: 'Prize Barcode Scanned',
    businessFlow: 'Business Flow',
    prizeBusinessFlow: 'Staff collects prize from member, scans barcode to award points',
    nowScanMemberQRForPrize: 'Now scan member QR code to award points for this prize',
    selectPrizeScanned: 'Select the prize that was collected',
    prizeEarnNote: 'Staff has collected this prize from member and is awarding points',
    earnFromPrize: 'Earn from Prize',
    memberGotPrize: 'Member got prize, award points',
    manualAdd: 'Manual Add',
    eventGift: 'Event gift, birthday bonus',
    memberWantsPrize: 'Member wants to redeem prize',
    manualPointsAddition: 'Manual Points Addition',
    pointsToAdd: 'Points to Add',
    reason: 'Reason',
    
    // Sequential Business Flow
    businessFlowSteps: 'Business Flow Steps',
    memberShowsQR: 'Member shows QR',
    staffScansPrize: 'Staff scans prize',
    staffScansMember: 'Staff scans member',
    systemUpdates: 'System updates',
    adminCanCheck: 'Admin can check',
    step1ScanPrize: 'Step 1: Scan prize barcode first',
    step2ScanMember: 'Step 2: Scan member QR code',
    step3Complete: 'Step 3: Transaction complete',
    step1Instruction: 'First, scan the prize barcode to identify points',
    step2Instruction: 'Now scan the member QR code to proceed',
    transactionComplete: 'Transaction completed successfully',
    step1Complete: 'Step 1 Complete - Prize Identified',
    step2Complete: 'Step 2 Complete - Ready for Transaction',
    waitingForMemberQR: 'Waiting for member QR code scan...',
    addPointsForPrize: 'Add Points (Prize Collection)',
    memberGotPrizeAddPoints: 'Member got this prize, award points',
    redeemThisPrize: 'Redeem This Prize',
    memberWantsThisPrize: 'Member wants to redeem this prize',
    startNewTransaction: 'Start New Transaction',
    
    // Point Adjustment
    adjustPoints: 'Adjust Points',
    adjustMemberPoints: 'Adjust Member Points',
    adjustmentType: 'Adjustment Type',
    addPoints: 'Add Points',
    subtractPoints: 'Subtract Points',
    increasePoints: 'Increase member points',
    decreasePoints: 'Decrease member points',
    pointsAmount: 'Points Amount',
    adjustmentReason: 'Adjustment Reason',
    preview: 'Preview',
    processing: 'Processing...',
    confirmAdjustment: 'Confirm Adjustment',
    
    // Barcode Management
    scanBarcode: 'Scan Barcode',
    pointsReward: 'Points Reward',
    readyForTransaction: 'Ready for transaction',
    cameraReadyForPrize: 'Camera ready - Click blue button to scan prize barcode',
    cameraReadyForMember: 'Camera ready - Click green button to scan member QR',
    scanningComplete: 'Scanning complete - Choose transaction type',
    itemRecognized: 'Item Recognized',
    earnPointsForPurchase: 'Earn points for purchase',
    itemPurchase: 'Item Purchase',
    confirmPurchase: 'Confirm Purchase',
    confirmItemPurchase: 'Confirm Item Purchase',
    memberPurchasedItem: 'Member purchased this item, award points',
    systemRecognizedPrize: 'System recognized prize and points',
    nowScanMemberQRToChooseAction: 'Now scan member QR code to choose action',
    reward: 'Reward',
    insufficientPointsAlert: 'Insufficient points! Need {need} points, currently have {current} points',
    insufficientPoints2: 'Insufficient points',
    systemRecognizedAsItem: 'System recognized as item',
    memberWillEarnPointsForPurchase: 'Member will earn points for this purchase',
    prizeCollection: 'Prize Collection',
    prizeRedemption: 'Prize Redemption',
    
    // Authentication
    signInToYourAccount: 'Sign in to your account',
    enterUsername: 'Enter your username',
    enterPassword: 'Enter your password',
    signingIn: 'Signing in',
    signIn: 'Sign In',
    signUp: 'Sign Up',
    demoAccounts: 'Demo Accounts',
    dontHaveAccount: "Don't have an account?",
    productionVersion: 'Production Version',
    secureAuthentication: 'Secure Authentication',
    createYourAccount: 'Create your account',
    confirmYourAccount: 'Confirm your account',
    enterEmail: 'Enter your email',
    enterFullName: 'Enter your full name',
    enterPhoneNumber: 'Enter your phone number',
    passwordRequirements: 'Minimum 8 characters with letters and numbers',
    confirmYourPassword: 'Confirm your password',
    passwordsDoNotMatch: 'Passwords do not match',
    passwordTooShort: 'Password must be at least 8 characters',
    creatingAccount: 'Creating account',
    createAccount: 'Create Account',
    alreadyHaveAccount: 'Already have an account?',
    confirmationCodeSent: 'We sent a confirmation code to',
    confirmationCode: 'Confirmation Code',
    confirming: 'Confirming',
    confirmAccount: 'Confirm Account',
    backToSignUp: 'Back to Sign Up',
    accountConfirmed: 'Account confirmed! You can now sign in.',
    
    // General
    successfully: 'Successfully',
    added: 'added',
    deducted: 'deducted',
    nowHas: 'now has',
    earned: 'earned',
    redeemed: 'redeemed',
    errorProcessingTransaction: 'Error processing transaction',
    errorAdjustingPoints: 'Error adjusting points',
    pleaseEnterValidPoints: 'Please enter valid points',
    pleaseEnterReason: 'Please enter adjustment reason',
    manualAdjustment: 'Manual Adjustment',
    retry: 'Retry',
    comingSoon: 'Coming Soon',
    thisFeatureIsBeingDeveloped: 'This feature is being developed',
    processing: 'Processing'
  },
  
  zh: {
    // Navigation
    home: '主頁',
    records: '記錄',
    profile: '個人資料',
    admin: '管理',
    
    // Common
    loading: '載入中...',
    save: '儲存',
    cancel: '取消',
    edit: '編輯',
    update: '更新',
    delete: '刪除',
    confirm: '確認',
    
    // Home Page
    yourPoints: '您的積分',
    keepEarning: '繼續賺取積分以解鎖獎勵！',
    memberQRCode: '您的會員二維碼',
    showQRCode: '向職員展示此二維碼以賺取或兌換積分',
    currentBranch: '當前分店',
    rewards: '獎勵',
    browseRewards: '瀏覽可用獎品',
    scan: '掃描',
    scanItems: '掃描商品以賺取積分',
    
    // Records Page
    transactionRecords: '交易記錄',
    all: '全部',
    earned: '已賺取',
    redeemed: '已兌換',
    currentBalance: '當前餘額',
    points: '積分',
    noTransactions: '未找到交易記錄',
    startEarning: '開始賺取積分以查看您的交易記錄！',
    totalEarned: '總賺取',
    totalRedeemed: '總兌換',
    balance: '餘額',
    
    // Profile Page
    profile: '個人資料',
    editProfile: '編輯個人資料',
    personalInfo: '個人信息',
    username: '用戶名',
    fullName: '全名',
    gender: '性別',
    selectGender: '選擇性別',
    male: '男性',
    female: '女性',
    other: '其他',
    birthday: '生日',
    phoneNumber: '電話號碼',
    email: '電子郵件',
    saveChanges: '儲存更改',
    saving: '儲存中...',
    security: '安全',
    changePassword: '更改密碼',
    currentPassword: '當前密碼',
    newPassword: '新密碼',
    confirmPassword: '確認新密碼',
    updating: '更新中...',
    updatePassword: '更新密碼',
    customerSupport: '客戶支援',
    aiAssistant: '智能助手',
    instantHelp: '即時獲得積分和獎勵相關幫助',
    languageSupport: '支援中文及英文',
    chatNow: '立即聊天',
    contactUs: '聯絡我們',
    whatsappContact: 'WhatsApp 支援',
    phoneContact: '電話支援',
    selectBranch: '選擇分店',
    
    // Admin Dashboard
    adminDashboard: '管理儀表板',
    scanner: '掃描器',
    redemption: '兌換',
    members: '會員',
    prizes: '獎品',
    items: '商品',
    settings: '設定',
    qrScanner: '二維碼掃描器',
    scanUserQR: '掃描用戶二維碼',
    scanItemBarcode: '掃描商品條碼',
    memberSearch: '會員搜尋',
    searchPlaceholder: '按姓名或用戶名搜尋...',
    viewHistory: '查看記錄',
    prizeManagement: '獎品管理',
    stock: '庫存',
    systemSettings: '系統設定',
    aiChatbot: '智能聊天機器人',
    enableChatbot: '啟用或停用客戶支援的智能聊天機器人',
    whatsappIntegration: 'WhatsApp 整合',
    enableWhatsapp: '透過 Twilio 啟用 WhatsApp 聊天機器人整合',
    instagramIntegration: 'Instagram 整合',
    enableInstagram: '透過 Facebook Graph API 啟用 Instagram 私訊聊天機器人整合',
    currentConfig: '當前配置',
    enabled: '已啟用',
    disabled: '已停用',
    branches: '分店',
    branchManagement: '分店管理',
    addBranch: '新增分店',
    branchName: '分店名稱',
    branchLocation: '分店位置',
    contactInfo: '聯絡資訊',
    whatsappNumber: 'WhatsApp 號碼',
    phoneNumber2: '電話號碼',
    
    // Messages
    profileUpdated: '個人資料更新成功！',
    passwordUpdated: '密碼更新成功！',
    settingsUpdated: '設定更新成功！',
    branchAdded: '分店新增成功！',
    branchUpdated: '分店更新成功！',
    passwordMismatch: '新密碼不匹配',
    accessDenied: '拒絕存取',
    noAdminPrivileges: '您沒有管理員權限。',
    
    // Chatbot
    aiAssistantDemo: '智能助手（演示）',
    online: '在線',
    typeMessage: '輸入訊息...',
    send: '發送',
    quickActions: '快速選項：',
    checkPoints: '查詢積分',
    availablePrizes: '可兌換獎品',
    pointsExpiry: '積分過期',
    call: '致電',
    
    // Layout & Navigation
    welcome: '歡迎',
    signOut: '登出',
    demo: '演示',
    
    // Admin Scanner
    demoClickToSimulate: '演示：點擊模擬掃描',
    simulateScan: '模擬掃描',
    scanResult: '掃描結果',
    addPoints: '新增積分',
    redeemPoints: '兌換積分',
    
    // Branch Management
    edit: '編輯',
    analytics: '分析',
    addNewBranch: '新增分店',
    
    // Form Labels
    name: '名稱',
    location: '位置',
    whatsapp: 'WhatsApp',
    phone: '電話',
    
    // Branch Actions
    editBranch: '編輯分店',
    deleteBranch: '刪除分店',
    confirmDelete: '您確定要刪除',
    branchDeleted: '分店刪除成功！',
    
    // User Management
    users: '用戶',
    userManagement: '用戶管理',
    addUser: '新增用戶',
    editUser: '編輯用戶',
    deleteUser: '刪除用戶',
    userRole: '用戶角色',
    admin: '管理員',
    sales: '銷售員',
    member: '會員',
    showAll: '顯示全部',
    loadingMembers: '載入會員中...',
    noMembersFound: '未找到會員',
    totalMembers: '總會員數',
    
    // Member Management
    memberManagement: '會員管理',
    addMember: '新增會員',
    editMember: '編輯會員',
    deleteMember: '刪除會員',
    memberDetails: '會員詳情',
    
    // Prize & Item Management
    addPrize: '新增獎品',
    editPrize: '編輯獎品',
    deletePrize: '刪除獎品',
    prizeName: '獎品名稱',
    costPoints: '所需積分',
    initialStock: '初始庫存',
    description: '描述',
    addItem: '新增商品',
    editItem: '編輯商品',
    deleteItem: '刪除商品',
    itemName: '商品名稱',
    itemManagement: '商品管理',
    barcode: '條碼',
    pointsValue: '積分價值',
    category: '類別',
    food: '食物',
    beverage: '飲品',
    dessert: '甜品',
    other: '其他',
    initialPoints: '初始積分',
    password: '密碼',
    
    // App Branding
    appName: 'ClawPoints 爪爪積分',
    appSlogan: '玩樂、積分、AI 智能換獎',
    
    // Login Page
    membershipPoints: 'ClawPoints 爪爪積分',
    demoPreview: '演示預覽',
    loginAsMember: '登入為演示用戶（會員）',
    loginAsSales: '登入為演示銷售員',
    loginAsAdmin: '登入為演示管理員（店主）',
    demoNotice: '這是演示預覽，無需真實身份驗證。',
    orLoginWithCredentials: '或使用會員帳號登入',
    memberLoginExample: '會員登入範例',
    
    // Pagination
    showing: '顯示',
    of: '共',
    previous: '上一頁',
    next: '下一頁',
    page: '頁',
    
    // Search
    searchMembers: '搜尋會員',
    searchPrizes: '搜尋獎品',
    searchItems: '搜尋商品',
    searchByName: '按名稱搜尋...',
    searchByNameOrBarcode: '按名稱或條碼搜尋...',
    noResults: '未找到結果',
    clearSearch: '清除搜尋',
    results: '結果',
    for: '搜尋',
    totalPrizes: '總獎品數',
    totalItems: '總商品數',
    
    // Home Page Modals
    availableRewards: '可兌換獎品',
    redeem: '兌換',
    redeemSuccess: '成功兌換',
    insufficientPoints: '積分不足',
    need: '需要',
    outOfStock: '缺貨',
    scanToEarn: '掃描賺取積分',
    scanInstructions: '掃描商品條碼',
    scanDescription: '將相機對準任何商品條碼即可立即賺取積分！',
    scanSuccess: '掃描成功！獲得',
    scanTip: '在真實應用中，這將使用您的相機掃描條碼',
    
    // My Account
    myAccount: '我的帳戶',
    accountInfo: '帳戶資訊',
    passwordManagement: '管理您的登入密碼和安全設定',
    accountActions: '帳戶操作',
    updateProfile: '更新個人資料',
    refreshSession: '刷新登入會話',
    sessionRefreshed: '會話刷新成功',
    
    // Scanner Business Flow
    memberScanned: '會員二維碼已掃描',
    itemScanned: '商品條碼已掃描',
    selectAction: '選擇操作',
    earnPoints: '賺取積分',
    redeemPrize: '兌換獎品',
    scanItemToEarn: '掃描商品條碼以新增積分',
    selectPrizeToRedeem: '選擇要兌換的獎品',
    selectItemScanned: '選擇已掃描的商品',
    nowScanMemberQR: '現在掃描會員的二維碼以獎勵積分',
    scanMemberQR: '掃描會員二維碼',
    itemNotFound: '找不到商品',
    itemNotInDatabase: '此條碼不在資料庫中',
    addToDatabase: '新增至資料庫',
    scanAgain: '重新掃描',
    simulateUnknown: '模擬未知商品',
    
    // Transaction History
    transactionHistory: '交易記錄',
    currentPoints: '當前積分',
    totalTransactions: '總交易數',
    currentBalance: '當前餘額',
    earned: '已賺取',
    redeemed: '已兌換',
    
    // Prize Business Flow
    prizeFlow: '禮品流程',
    memberFlow: '會員流程',
    prizeFlowDesc: '1. 掃描禮品條碼 → 2. 掃描會員QR → 加點數',
    memberFlowDesc: '1. 掃描會員QR → 2. 選擇操作 → 完成交易',
    scanPrizeBarcode: '掃描禮品條碼',
    scanBarcode: '掃描條碼',
    prizeScanned: '禮品條碼已掃描',
    businessFlow: '業務流程',
    prizeBusinessFlow: '店員收回會員夾到的獎品，掃描條碼為會員加點',
    nowScanMemberQRForPrize: '現在掃描會員QR碼為此禮品獎勵積分',
    selectPrizeScanned: '選擇已收回的禮品',
    prizeEarnNote: '店員已收回此禮品並正在獎勵積分',
    earnFromPrize: '禮品加點',
    memberGotPrize: '會員夾到禮品，獎勵積分',
    manualAdd: '手動加點',
    eventGift: '活動贈送、生日禮物',
    memberWantsPrize: '會員要兌換獎品',
    manualPointsAddition: '手動加點數',
    pointsToAdd: '要加的點數',
    reason: '原因',
    
    // Sequential Business Flow
    businessFlowSteps: '業務流程步驟',
    memberShowsQR: '會員出示QR',
    staffScansPrize: '店員掃禮品條碼',
    staffScansMember: '店員掃會員QR',
    systemUpdates: '系統更新資料',
    adminCanCheck: '店主可查紀錄',
    step1ScanPrize: '步驟1: 先掃描禮品條碼',
    step2ScanMember: '步驟2: 掃描會員QR碼',
    step3Complete: '步驟3: 交易完成',
    step1Instruction: '首先，掃描禮品條碼以識別點數',
    step2Instruction: '現在掃描會員QR碼以繼續',
    transactionComplete: '交易成功完成',
    step1Complete: '步驟1完成 - 禮品已識別',
    step2Complete: '步驟2完成 - 準備交易',
    waitingForMemberQR: '等待掃描會員QR碼...',
    addPointsForPrize: '加點數（收回禮品）',
    memberGotPrizeAddPoints: '會員夾到此禮品，獎勵積分',
    redeemThisPrize: '兌換此獎品',
    memberWantsThisPrize: '會員要兌換此獎品',
    startNewTransaction: '開始新交易',
    
    // Point Adjustment
    adjustPoints: '調整積分',
    adjustMemberPoints: '調整會員積分',
    adjustmentType: '調整類型',
    addPoints: '增加積分',
    subtractPoints: '扣除積分',
    increasePoints: '增加會員積分',
    decreasePoints: '減少會員積分',
    pointsAmount: '積分數量',
    adjustmentReason: '調整原因',
    preview: '預覽',
    processing: '處理中...',
    confirmAdjustment: '確認調整',
    
    // Barcode Management
    scanBarcode: '掃描條碼',
    pointsReward: '獎勵積分',
    readyForTransaction: '準備交易',
    cameraReadyForPrize: '相機就緒 - 點擊藍色按鈕掃描禮品條碼',
    cameraReadyForMember: '相機就緒 - 點擊綠色按鈕掃描會員QR',
    scanningComplete: '掃描完成 - 選擇交易類型',
    itemRecognized: '商品已識別',
    earnPointsForPurchase: '購買獲得積分',
    itemPurchase: '商品購買',
    confirmPurchase: '確認購買',
    confirmItemPurchase: '確認商品購買',
    memberPurchasedItem: '會員購買此商品，獎勵積分',
    systemRecognizedPrize: '系統已識別禮品和點數',
    nowScanMemberQRToChooseAction: '現在請掃描會員的QR碼以選擇操作',
    reward: '獎勵',
    insufficientPointsAlert: '積分不足！需要 {need} 積分，目前只有 {current} 積分',
    insufficientPoints2: '積分不足',
    systemRecognizedAsItem: '系統識別為商品',
    memberWillEarnPointsForPurchase: '會員購買此商品將獲得積分獎勵',
    prizeCollection: '收回禮品',
    prizeRedemption: '兌換獎品',
    
    // Authentication
    signInToYourAccount: '登入您的帳戶',
    enterUsername: '輸入用戶名',
    enterPassword: '輸入密碼',
    signingIn: '登入中',
    signIn: '登入',
    signUp: '註冊',
    demoAccounts: '演示帳戶',
    dontHaveAccount: '還沒有帳戶？',
    productionVersion: '正式版本',
    secureAuthentication: '安全認證',
    createYourAccount: '創建您的帳戶',
    confirmYourAccount: '確認您的帳戶',
    enterEmail: '輸入電子郵件',
    enterFullName: '輸入全名',
    enterPhoneNumber: '輸入電話號碼',
    passwordRequirements: '最少8個字符，包含字母和數字',
    confirmYourPassword: '確認密碼',
    passwordsDoNotMatch: '密碼不匹配',
    passwordTooShort: '密碼必須至少8個字符',
    creatingAccount: '創建帳戶中',
    createAccount: '創建帳戶',
    alreadyHaveAccount: '已有帳戶？',
    confirmationCodeSent: '我們已發送確認碼至',
    confirmationCode: '確認碼',
    confirming: '確認中',
    confirmAccount: '確認帳戶',
    backToSignUp: '返回註冊',
    accountConfirmed: '帳戶已確認！您現在可以登入。',
    
    // General
    successfully: '成功',
    added: '增加',
    deducted: '扣除',
    nowHas: '現在有',
    earned: '賺取',
    redeemed: '兌換',
    errorProcessingTransaction: '處理交易時發生錯誤',
    errorAdjustingPoints: '調整積分時發生錯誤',
    pleaseEnterValidPoints: '請輸入有效的積分',
    pleaseEnterReason: '請輸入調整原因',
    manualAdjustment: '手動調整',
    retry: '重試',
    comingSoon: '即將推出',
    thisFeatureIsBeingDeveloped: '此功能正在開發中',
    processing: '處理中'
  }
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    const saved = localStorage.getItem('app-language');
    return saved || 'zh'; // Default to Chinese
  });

  const changeLanguage = (newLanguage) => {
    setLanguage(newLanguage);
    localStorage.setItem('app-language', newLanguage);
  };

  const t = (key) => {
    return translations[language]?.[key] || key;
  };

  const value = {
    language,
    changeLanguage,
    t,
    isEnglish: language === 'en',
    isChinese: language === 'zh'
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};